import {
  AHIcon,
  AHSeparator,
  Box,
  ListItem,
  useBoxStyle,
  useTheme,
} from "arkh";
import React, { useMemo } from "react";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../components/mobile/QueryNetworkBoundrary";

import { TeamIconCircle } from "../../components/mobile/TeamIconCircle";

import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";

import { ScrollView } from "react-native-gesture-handler";
import {
  NativeStackNavigationProp,
  createNativeStackNavigator,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import {
  MoreItem,
  useNavigationHeader,
  useNavigationHeaderOptions,
} from "../../common/navigationHeaderOptions";
import { TeamEditIdentifiersScreenComponent } from "./TeamAdminIdentifiersScreen";
import { TeamAdminLayoutScreen } from "./TeamAdminLayout";

import { useIntl, FormattedMessage } from "react-intl";

import { TeamAdminUnarchiveTeamMutation } from "./__generated__/TeamAdminUnarchiveTeamMutation.graphql";
import { TeamAdminQuery } from "./__generated__/TeamAdminQuery.graphql";
import { TeamAdminArchiveTeamMutation } from "./__generated__/TeamAdminArchiveTeamMutation.graphql";
import { TeamAdmin_Team$key } from "./__generated__/TeamAdmin_Team.graphql";
import { ArchivedCard } from "./ArchivedCard";
import { ArchivedController } from "./ArchivedController";
import { TeamAdminWhitelistUser } from "./TeamAdminWhitelistUser";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useHasFeatureFlag } from "@/src/useHasFeatureFlag";
import { FeatureFlags } from "@/src/config";

function TeamAdminComponent(
  props: {
    team: TeamAdmin_Team$key;
  } & {
    onEditTeam: () => void;
    onEditLayout: () => void;
    onEditTeamSchedule: () => void;
    onEditRoles: () => void;
    onEditWhitelistUser: () => void;
  }
) {
  const team = useFragment(
    graphql`
      fragment TeamAdmin_Team on Team {
        id
        name
        icon
        isArchived
        isPrivate
        ...TeamIconCircleComponent_Team
      }
    `,
    props.team
  );

  const theme = useTheme();

  const contentContainerStyle = useBoxStyle({
    space: "medium",
    color: "surface-background",
  });

  const intl = useIntl();
  const unarchiveTitle = intl.formatMessage({
    defaultMessage: "Unarchive Team",
    description: "TeamAdmin Unarchive Team button",
  });

  const [unarchiveTeam, loading] = useMutation<TeamAdminUnarchiveTeamMutation>(
    graphql`
      mutation TeamAdminUnarchiveTeamMutation($teamId: ID!) {
        team(id: $teamId) {
          updateArchivedStatus(isArchived: false) {
            id
            isArchived
            members {
              id
            }
          }
        }
      }
    `
  );

  const intl_tokens = useIntlTokens();
  const hasEmployeeRole = useHasFeatureFlag(FeatureFlags.EmployeeRole);

  return (
    <ScrollView
      style={{ flex: 1, backgroundColor: theme["surface-background"] }}
      contentContainerStyle={contentContainerStyle}
    >
      <ArchivedController
        archived={team.isArchived}
        archivedCard={
          <ArchivedCard
            title={
              <FormattedMessage
                defaultMessage="Archived Team"
                description="TeamCustomization archived team title"
              />
            }
            onPress={() => {
              unarchiveTeam({
                variables: {
                  teamId: team.id,
                },
              });
            }}
            loading={loading}
            unarchiveTitle={unarchiveTitle}
          />
        }
      >
        <ListItem
          isFirst
          left={<TeamIconCircle team={team} size="smallMedium" />}
          right={<ListItem.NavArrow />}
          onPress={() => {
            props.onEditTeam();
          }}
        >
          <ListItem.Title>{team.name}</ListItem.Title>
          <ListItem.SubTitle>
            <FormattedMessage
              defaultMessage="Change name, color and icon"
              description="TeamCustomization edit team subtitle"
            />
          </ListItem.SubTitle>
        </ListItem>
        <AHSeparator />
        <ListItem
          isLast
          left={
            <AHIcon type="team-layout" size="large" color="text-secondary" />
          }
          right={<ListItem.NavArrow />}
          onPress={() => {
            props.onEditLayout();
          }}
        >
          <ListItem.Title>
            <FormattedMessage
              defaultMessage="Layout"
              description="TeamCustomization layout title"
            />
          </ListItem.Title>
          <ListItem.SubTitle>
            <FormattedMessage
              defaultMessage="Sort or hide team features"
              description="TeamCustomization layout subtitle"
            />
          </ListItem.SubTitle>
        </ListItem>

        <Box space="medium" />
        <ListItem
          isFirst
          isLast={!hasEmployeeRole}
          left={
            <AHIcon type="team-schedule" size="large" color="text-secondary" />
          }
          right={<ListItem.NavArrow />}
          onPress={() => {
            props.onEditTeamSchedule();
          }}
        >
          <ListItem.Title>
            <FormattedMessage
              defaultMessage="Edit Team Schedule"
              description="TeamCustomization edit team schedule title"
            />
          </ListItem.Title>
        </ListItem>
        {hasEmployeeRole ? (
          <>
            <AHSeparator />
            <ListItem
              isLast
              left={
                <AHIcon
                  type="employee-role"
                  size="large"
                  color="text-secondary"
                />
              }
              right={<ListItem.NavArrow />}
              onPress={() => {
                props.onEditRoles();
              }}
            >
              <ListItem.Title>{intl_tokens.EditLineup}</ListItem.Title>
            </ListItem>
          </>
        ) : null}
        <Box space="medium" />
        {team.isPrivate ? (
          <ListItem
            isFree
            left={<AHIcon type="users" size="large" color="text-secondary" />}
            right={
              <Box style={{ flex: 1, alignItems: "flex-end" }}>
                <ListItem.NavArrow />
              </Box>
            }
            onPress={() => {
              props.onEditWhitelistUser();
            }}
          >
            <ListItem.Title>{intl_tokens.AssignPermissions}</ListItem.Title>
          </ListItem>
        ) : null}
      </ArchivedController>
    </ScrollView>
  );
}

function InnerTeamAdminMain(
  props: {
    fetchKey: string;
  } & TeamAdminScreenProp<"TeamAdminMain">
) {
  const data = useLazyLoadQuery<TeamAdminQuery>(
    graphql`
      query TeamAdminQuery($teamId: ID!) {
        store {
          team(id: $teamId) {
            id
            name
            isPrivate
            isArchived
            ...TeamAdmin_Team
          }
        }
      }
    `,
    {
      teamId: props.route.params.teamId,
    },
    {
      fetchKey: props.fetchKey,
    }
  );

  const [setArchiveStatus] = useMutation<TeamAdminArchiveTeamMutation>(graphql`
    mutation TeamAdminArchiveTeamMutation($isArchived: Boolean!, $teamId: ID!) {
      team(id: $teamId) {
        updateArchivedStatus(isArchived: $isArchived) {
          id
          isArchived
          members {
            id
          }
        }
      }
    }
  `);

  const intl = useIntl();

  const title = intl.formatMessage({
    defaultMessage: "Archive Team",
    description: "TeamAdminSection: Danger Zone Archive Team btn",
  });

  const more = useMemo(() => {
    const m: MoreItem[] = [];
    if (!data.store.team?.isArchived) {
      m.push({
        icon: "archive",
        label: title,
        onPress() {
          setArchiveStatus({
            variables: {
              isArchived: true,
              teamId: data.store.team!.id,
            },
          });
        },
      });
    }
    return m.length > 0 ? m : undefined;
  }, [data.store.team, title, setArchiveStatus]);

  useNavigationHeader({
    type: "DONE",
    title: data.store.team?.name ?? "",
    onDone: () => {
      props.navigation.goBack();
    },
    more: more,
  });

  return data.store.team ? (
    <TeamAdminComponent
      team={data.store.team}
      onEditTeam={() => {
        props.navigation.navigateDeprecated("TeamAdminIdentifiers", {
          teamId: data.store.team!.id,
        });
      }}
      onEditLayout={() => {
        props.navigation.navigateDeprecated("TeamAdminLayout", {
          teamId: data.store.team!.id,
        });
      }}
      onEditTeamSchedule={() => {
        props.navigation.navigateDeprecated("TeamScheduleAdmin", {
          teamId: data.store.team!.id,
        });
      }}
      onEditRoles={() => {
        props.navigation.navigate("EmployeeRolesAdmin", {
          teamId: data.store.team!.id,
        });
      }}
      onEditWhitelistUser={() => {
        props.navigation.navigateDeprecated("TeamAdminWhitelistUser", {
          teamId: data.store.team!.id,
        });
      }}
    />
  ) : null;
}

type ParamList = {
  TeamAdminMain: { teamId: string };
  TeamAdminLayout: { teamId: string };
  TeamAdminIdentifiers: { teamId: string };
  TeamAdminWhitelistUser: { teamId: string };
};
const Stack = createNativeStackNavigator<ParamList>();

export type TeamAdminScreenProp<T extends keyof ParamList> = {
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, T>,
    UserLoggedInNavigatorNavigationProp<"TeamAdmin">
  >;
  route: RouteProp<ParamList, T>;
};

function TeamAdminMain(props: TeamAdminScreenProp<"TeamAdminMain">) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerTeamAdminMain {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function TeamAdminIdentifiers(
  props: TeamAdminScreenProp<"TeamAdminIdentifiers">
) {
  return (
    <TeamEditIdentifiersScreenComponent
      navigation={props.navigation}
      teamId={props.route.params.teamId}
    />
  );
}

export const TeamAdmin = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"TeamAdmin">) => {
    const navigationOptions = useNavigationHeaderOptions();
    const theme = useTheme();

    return (
      <Stack.Navigator
        screenOptions={{
          contentStyle: {
            backgroundColor: theme["surface-background"],
          },
          title: "",
        }}
      >
        <Stack.Screen
          component={TeamAdminMain}
          name="TeamAdminMain"
          initialParams={{
            teamId: props.route.params.teamId,
          }}
          options={{
            ...navigationOptions({
              type: "DONE",
              title: "",
              onDone: () => {
                props.navigation.goBack();
              },
            }),
          }}
        />
        <Stack.Screen
          component={TeamAdminIdentifiers}
          name="TeamAdminIdentifiers"
        />
        <Stack.Screen
          component={TeamAdminLayoutScreen}
          name="TeamAdminLayout"
        />
        <Stack.Screen
          component={TeamAdminWhitelistUser}
          name="TeamAdminWhitelistUser"
        />
      </Stack.Navigator>
    );
  },
  false
);
